export function compactFlexCurrentAnswer(currentAnswer) {
  if (
    !currentAnswer ||
    typeof currentAnswer !== 'object' ||
    Array.isArray(currentAnswer)
  ) {
    return currentAnswer;
  }
  return Object.fromEntries(
    Object.entries(currentAnswer).map(([group, fields]) => [
      group,
      fields && typeof fields === 'object' && !Array.isArray(fields)
        ? Object.fromEntries(
            Object.entries(fields).filter(([, value]) => value !== ''),
          )
        : fields,
    ]),
  );
}

/**
 * Format scraped EMR questions into the section metadata the Instruction
 * Assistant sends with its plan/generate calls. This mirrors inline autogen's
 * `sections_to_generate` shape ({ key, primer, type, choices, replyType,
 * currentAnswer }; see ConversationDetailsCX.scrapeTemplate), plus only the
 * View More subsection names — never the full `structure`.
 *
 * The raw EMR fields are PascalCase (Key/Question/Type/Choices/ReplyType); they
 * MUST be emitted lowercase or the backend silently drops the metadata. Rows
 * without a Key are skipped, and undefined fields are omitted.
 */
export function buildSectionMetadata(questions, subsectionsBySection = {}) {
  return (questions || [])
    .filter((q) => q && q.Key)
    .map((q) => {
      const meta = {
        key: q.Key,
        primer: q.Question,
        type: q.Type,
        choices: q.Choices,
        replyType: q.ReplyType,
        currentAnswer: q.currentAnswer,
      };
      const subsectionNames = Object.keys(subsectionsBySection[q.Key] || {});
      if (subsectionNames.length > 0) {
        meta.subsections = subsectionNames;
      }
      return Object.fromEntries(
        Object.entries(meta).filter(([, value]) => value !== undefined),
      );
    });
}

/**
 * Ask the backend to turn the kept changes [{directive, sections}] into revised
 * section instructions and examples. Throws on an error response; returns both
 * changed-section maps.
 */
export async function requestInstructionRevisions(
  apiClient,
  configId,
  keptChanges,
  sectionMetadata,
  feedbackContext = {},
  instructionChangeEvent = null,
) {
  const rsp = await apiClient.generateInstructionRevisions(
    configId,
    keptChanges,
    sectionMetadata,
    feedbackContext,
    instructionChangeEvent,
  );
  if (rsp && rsp.error) {
    throw new Error(
      typeof rsp.error === 'string'
        ? rsp.error
        : 'Could not update the preferences.',
    );
  }
  return {
    newInstructions: (rsp && rsp.newInstructions) || {},
    newExamples: (rsp && rsp.newExamples) || {},
  };
}
